import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { onAuthStateChanged, getIdToken, signOut } from 'firebase/auth';
import { auth } from './components/firebase_utils/firebase';
import useApi from './Api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  const apiClient = useApi();
  const prevUserRef = useRef(null);

  const refreshToken = useCallback(async () => {
    if (auth.currentUser) {
      try {
        // 1) Get a fresh token
        const newToken = await getIdToken(auth.currentUser, true);
        const tokenResult = await auth.currentUser.getIdTokenResult();
        setToken(newToken);

        // 2) Fetch user info
        const userInfoResponse = await apiClient.get('/user-me', {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        const { role, support, premium, subscription, username } =
          userInfoResponse.data;

        setRole(role);
        setUser({
          ...auth.currentUser,
          isSupport: !!support,
          isPremium: !!premium,
          subscription,
          username,
        });
        setShowUsernameModal(!username);

        // 3) Schedule next refresh
        const expirationTimeMs = new Date(tokenResult.expirationTime).getTime();
        const timeUntilExpiration = expirationTimeMs - Date.now() - 60000; // 1 minute early
        if (timeUntilExpiration > 0) {
          setTimeout(() => {
            refreshToken();
          }, timeUntilExpiration);
        }
      } catch (error) {
        // If our user fetch fails:
        if (error.response && error.response.status === 429) {
          setRole('user');
          setUser({
            ...auth.currentUser,
            isSupport: false,
            isPremium: false,
            subscription: null,
            username: null,
          });
        } else {
          setRole(null);
          setUser({
            ...auth.currentUser,
            isSupport: false,
            isPremium: false,
            subscription: null,
            username: null,
          });
        }
        setShowUsernameModal(false);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (prevUserRef.current && prevUserRef.current.uid === currentUser.uid) {
          setLoading(false);
          return;
        }
        try {
          // First token fetch
          const currentToken = await getIdToken(currentUser);
          const tokenResult = await currentUser.getIdTokenResult();
          setToken(currentToken);

          // Fetch user info
          const userInfoResponse = await apiClient.get('/user-me', {
            headers: { Authorization: `Bearer ${currentToken}` },
          });
          const { role, support, premium, subscription, username } =
            userInfoResponse.data;

          setRole(role);
          setUser({
            ...currentUser,
            isSupport: !!support,
            isPremium: !!premium,
            subscription,
            username,
          });

          setShowUsernameModal(!username);

          // Schedule the refresh
          const expirationTimeMs = new Date(tokenResult.expirationTime).getTime();
          const timeUntilExpiration = expirationTimeMs - Date.now() - 60000;
          if (timeUntilExpiration > 0) {
            setTimeout(() => {
              refreshToken();
            }, timeUntilExpiration);
          }

          prevUserRef.current = currentUser;
        } catch (error) {
          if (error.response && error.response.status === 429) {
            setRole('user');
            setUser({
              ...currentUser,
              isSupport: false,
              isPremium: false,
              subscription: null,
              username: null,
            });
          } else {
            setRole(null);
            setUser({
              ...currentUser,
              isSupport: false,
              isPremium: false,
              subscription: null,
              username: null,
            });
          }
          setShowUsernameModal(false);
        }
      } else {
        setUser(null);
        setRole(null);
        setToken(null);
        setShowUsernameModal(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [apiClient, refreshToken]);

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      setRole(null);
      setToken(null);
      prevUserRef.current = null;
      setShowUsernameModal(false);
    } catch (error) {
      throw error;
    }
  };

  const hideUsernameModal = () => {
    if (user && user.username) {
      setShowUsernameModal(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        token,
        loading,
        refreshToken,
        logout,
        showUsernameModal,
        setShowUsernameModal,
        hideUsernameModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
